// here i  need one video trimmer component which will accept video url and element and it will update the startTime and endTime of the elenment, you can get that elelemnt by using the element id store.getElementsById(elementId),
// i need Ui like it will show video and in the bottom there should be one video trimmer where i can easily trim the video by looking in to the video, and there should be one button to save the changes and one button to cancel the changes,

import React, { useEffect, useRef, useState } from 'react';
import { Button, Space, Slider, Modal } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, ScissorOutlined } from '@ant-design/icons';
import './sceneMediaTrimmer.less';
import { JSX } from 'react/jsx-runtime';

interface SceneMediaTrimmerProps {
  videoUrl: string;
  elementId: string;
  store: any;
  pageId: string;
  onsave: () => void;
  onCancel: () => void;
}

const SceneMediaTrimmer: React.FC<SceneMediaTrimmerProps> = ({
  videoUrl,
  elementId,
  store,
  pageId,
  onsave,
  onCancel
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // These are stored as seconds for UI
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  // These are fractions (0-1) for the element
  const [startTimeFraction, setStartTimeFraction] = useState(0);
  const [endTimeFraction, setEndTimeFraction] = useState(1);
  const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);

  useEffect(() => {
    const element = store.getElementById(elementId);
    if (element && videoRef.current) {
      // Get fractions from the element (0-1 range)
      const startFraction = element.startTime || 0;
      const endFraction = element.endTime || 1;
      
      // Store fractions for later use when saving
      setStartTimeFraction(startFraction);
      setEndTimeFraction(endFraction);
      
      // Convert to seconds for UI display when video duration is known
      if (duration > 0) {
        setStartTime(startFraction * duration);
        setEndTime(endFraction * duration);
      }
    }
  }, [elementId, store, duration]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = currentTime;
    }
  }, [currentTime]);

  useEffect(() => {
    // Force video to load when component mounts
    if (videoRef.current) {
      const video = videoRef.current;
      
      // Try to improve buffering with these settings
      video.preload = "auto";
      video.autoplay = false;
      video.playsInline = true;
      
      // Clear any previous sources
      video.src = "";
      
      // Set new source with cache buster
      setTimeout(() => {
        if (videoRef.current) {
          // Add cache buster to URL to avoid caching issues
          const cacheBuster = `?cb=${Date.now()}`;
          const sourceWithCache = videoUrl.includes('?') 
            ? `${videoUrl}&cb=${Date.now()}` 
            : `${videoUrl}${cacheBuster}`;
          
          videoRef.current.src = sourceWithCache;
          videoRef.current.load();
        }
      }, 100);
    }
  }, [videoUrl]);

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const videoDuration = videoRef.current.duration;
      setDuration(videoDuration);
      
      // Now that we know duration, convert fractions to seconds
      const element = store.getElementById(elementId);
      if (element) {
        const startFraction = element.startTime || 0;
        const endFraction = element.endTime || 1;
        
        setStartTime(startFraction * videoDuration);
        setEndTime(endFraction * videoDuration);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handlePlayPause = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      // If at end, reset to start
      if (videoRef.current.currentTime >= endTime) {
        videoRef.current.currentTime = startTime;
      }
      
      // Play immediately without reloading to avoid buffering again
      const playPromise = videoRef.current.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsPlaying(true);
          })
          .catch(error => {
            console.error("Error playing video:", error);
          });
      }
    }
  };

  // When slider values change, update seconds and fractions
  const handleSliderChange = (values: number[]) => {
    const [newStartSec, newEndSec] = values as [number, number];
    
    // Update seconds for UI
    setStartTime(newStartSec);
    setEndTime(newEndSec);
    
    // Also update fractions since they're needed when saving
    if (duration > 0) {
      setStartTimeFraction(Number((newStartSec / duration).toFixed(16)));
      setEndTimeFraction(Number((newEndSec / duration).toFixed(16)));
    }
  };

  const handleSave = () => {
    setIsConfirmModalVisible(true);
  };

  const handleConfirmSave = (updatePageDuration: boolean) => {
    const element = store.getElementById(elementId);
    const page = store.getElementById(pageId);
    
    if (element) {
      // Always save as fractions between 0-1 with precision limited to 16 decimal places
      element.set({
        startTime: Number(startTimeFraction.toFixed(16)),
        endTime: Number(endTimeFraction.toFixed(16))
      });

      if (updatePageDuration) {
        // Calculate duration in milliseconds for the page
        const durationMs = (endTime - startTime) * 1000;
        page.set({
          duration: durationMs
        });
      }

      setIsConfirmModalVisible(false);
      onsave();
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const milliseconds = Math.floor((time % 1) * 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
  };

  return (
    <div className="video-trimmer">
      <div className="video-preview" style={{ 
        position: 'relative',
        width: '100%'
      }}>
        <video
          ref={videoRef}
          onLoadedMetadata={handleLoadedMetadata}
          // onTimeUpdate={handleTimeUpdate}
          onEnded={() => setIsPlaying(false)}
          preload="auto"
          playsInline
          style={{ 
            width: '100%',
            display: 'block'
          }}
          controls
          muted
        />
        {/* <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          cursor: 'pointer'
        }}>
          <div 
            onClick={handlePlayPause}
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#6F4CFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '3px solid white',
              boxShadow: '0px 0px 8px rgba(0,0,0,0.3)'
            }}
          >
            {isPlaying ? (
              <div style={{ 
                width: '18px', 
                height: '18px', 
                backgroundColor: 'white',
                borderRadius: '2px'
              }}></div>
            ) : (
              <div style={{ 
                width: '0', 
                height: '0', 
                borderTop: '10px solid transparent',
                borderBottom: '10px solid transparent',
                borderLeft: '16px solid white',
                marginLeft: '4px'
              }}></div>
            )}
          </div>
        </div> */}
      </div>

      <div className="trimmer-controls">
        <div className="time-display">
          <span>{formatTime(startTime)}</span>
          <span>{formatTime(endTime)}</span>
        </div>
        
        <div className="trimmer-slider">
          <Slider
            range
            min={0}
            max={duration}
            step={0.001}
            value={[startTime, endTime]}
            onChange={handleSliderChange}
            tooltip={{ formatter: (value?: number) => value ? formatTime(value) : '' }}
            trackStyle={[
              { backgroundColor: '#1890ff' },
              { backgroundColor: '#1890ff' }
            ]}
            railStyle={{ backgroundColor: '#f0f0f0' }}
          />
        </div>

        <div className="action-buttons">
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" onClick={handleSave}>Save Changes</Button>
          </Space>
        </div>
      </div>

      <Modal
        title="Update Page Duration"
        open={isConfirmModalVisible}
        onCancel={() => setIsConfirmModalVisible(false)}
        footer={[
          <Button key="no" onClick={() => handleConfirmSave(false)}>
            No, Keep Current Duration
          </Button>,
          <Button key="yes" type="primary" onClick={() => handleConfirmSave(true)}>
            Yes, Update Duration
          </Button>
        ]}
      >
        <p>Do you want to update the page duration to match the trimmed video duration?</p>
        <p>Current video length: {formatTime(duration)}</p>
        <p>Trimmed length: {formatTime(endTime - startTime)}</p>
        <p className="note">
          <strong>Note:</strong> Start time will be {formatTime(startTime)} ({(startTimeFraction * 100).toFixed(2)}%)
          and end time will be {formatTime(endTime)} ({(endTimeFraction * 100).toFixed(2)}%)
        </p>
      </Modal>
    </div>
  );
};

export default SceneMediaTrimmer;